import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
  Filter,
  ExcelExport,
  PdfExport,
  FilterType,
} from '@syncfusion/ej2-react-grids';
import { Box, Typography } from '@mui/material';

import { useGetPoliciesQuery } from '../../../../../services/CaseManagementService';
import { usePolicySearch, usePolicyNavigation } from './hooks';
import {
  categoryTemplate,
  policyNameTemplate,
  policyNumberTemplate,
  createDateTemplate,
} from './templates';
import { SearchBar } from './components';

export interface PolicyGridProps {
  testId?: string;
}

// Grid settings
const pageSettings = { pageSize: 25, pageSizes: [10, 25, 50, 100] };
const filterSettings = { type: 'Excel' as FilterType };
const sortSettings = { allowUnsort: false };

// Grid container styles
const gridContainerStyles = {
  '& .e-grid': {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontFamily: 'inherit',
  },
  '& .e-grid .e-gridheader': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .e-grid .e-headercell': {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '14px',
  },
  '& .e-grid .e-row': {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  },
  '& .e-grid .e-altrow': {
    backgroundColor: '#ffffff',
  },
};

/**
 * PolicyGrid - Displays policies for case management
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search and navigation
 * - Clean component structure
 */
export const PolicyGrid: React.FC<PolicyGridProps> = ({ testId }) => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';

  // Fetch policies for the company
  const {
    data: policies = [],
    isLoading,
    error,
  } = useGetPoliciesQuery(
    { company },
    {
      skip: !company,
    }
  );

  // Use custom hooks
  const { searchTerm, filteredPolicies, handleSearchChange, handleClearSearch } =
    usePolicySearch(policies);
  const { handleRowSelected } = usePolicyNavigation(company);

  // Date templates
  const effectiveDateTemplate = createDateTemplate('effectiveDate');
  const reviewDateTemplate = createDateTemplate('reviewDate');

  // No company state
  if (!company) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">
          No company specified. Cannot load policies.
        </Typography>
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading policies...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Error loading policies. Please try again.</Typography>
      </Box>
    );
  }

  // Empty state
  if (policies.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            placeholder="Search policies by name, number, category..."
          />
        </Box>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No policies found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No policies available for {company}
          </Typography>
        </Box>
      </Box>
    );
  }

  // No search results
  if (filteredPolicies.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            placeholder="Search policies by name, number, category..."
          />
        </Box>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No policies match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          placeholder="Search policies by name, number, category..."
        />
      </Box>

      {/* Results Summary */}
      {searchTerm.trim() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing {filteredPolicies.length} of {policies.length} policies
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      )}

      {/* Grid */}
      <Box sx={gridContainerStyles}>
        <GridComponent
          dataSource={filteredPolicies}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          allowExcelExport={true}
          allowPdfExport={true}
          pageSettings={pageSettings}
          filterSettings={filterSettings}
          sortSettings={sortSettings}
          height="500px"
          rowSelected={handleRowSelected}
          data-testid={testId}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="policyName"
              headerText="Policy Name"
              width="200"
              textAlign="Left"
              template={policyNameTemplate}
            />
            <ColumnDirective
              field="policyNumber"
              headerText="Policy Number"
              width="150"
              textAlign="Left"
              template={policyNumberTemplate}
            />
            <ColumnDirective
              field="category"
              headerText="Category"
              width="130"
              textAlign="Center"
              template={categoryTemplate}
              allowFiltering={true}
            />
            <ColumnDirective
              field="effectiveDate"
              headerText="Effective Date"
              width="140"
              textAlign="Center"
              template={effectiveDateTemplate}
            />
            <ColumnDirective
              field="reviewDate"
              headerText="Review Date"
              width="140"
              textAlign="Center"
              template={reviewDateTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort, Filter, ExcelExport, PdfExport]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default PolicyGrid;
